import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const langToExt: Record<string, string> = {
  javascript: '.js',
  typescript: '.ts',
  python: '.py',
  html: '.html',
  css: '.css',
  json: '.json',
  markdown: '.md',
};

interface SaveNewFileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (path: string) => void;
  fileName: string;
  language: string;
  basePath?: string;
}

export const SaveNewFileDialog: React.FC<SaveNewFileDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  fileName,
  language,
  basePath = '',
}) => {
  const [path, setPath] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      const ext = langToExt[language] || '';
      const initialValue = `${basePath}${fileName}${ext}`;
      setPath(initialValue);
      
      setTimeout(() => {
        if (inputRef.current) {
          const selectionEnd = ext ? initialValue.length - ext.length : initialValue.length;
          inputRef.current.focus();
          inputRef.current.setSelectionRange(0, selectionEnd);
        }
      }, 100);
    }
  }, [isOpen, fileName, language, basePath]);

  const handleSave = () => {
    if (path.trim()) {
      onSave(path.trim());
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save New File</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Label htmlFor="file-path">Path and Name</Label>
          <Input
            ref={inputRef}
            id="file-path"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="path/to/your/file.js"
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};